/**
 * Kind → tone (TMMAPP-0088, pattern 8). Soft fill and ink come from `toneColours`; this map adds
 * no tokens. Account `CASH` and envelope `CASH` are the same kind. `SAVINGS` is success (set aside,
 * distinct from the cash pool) and `BUDGET` is warning (expecting to spend). Item kinds, actor
 * kinds and flags are not in this set — callers paint those with a neutral `KindChip`.
 */
import type { Tone } from '@/theme/statusTones';

export const KINDS = [
  'CASH',
  'CREDIT_CARD',
  'REVOLVING',
  'LOAN',
  'ASSET',
  'TRADABLE',
  'INCOME',
  'EXPENSE',
  'SAVINGS',
  'BUDGET',
] as const;

export type Kind = (typeof KINDS)[number];

export type KindTone = Exclude<Tone, 'draft'>;

const KIND_TONE: Record<Kind, KindTone> = {
  CASH: 'info',
  CREDIT_CARD: 'warning',
  REVOLVING: 'warning',
  LOAN: 'error',
  ASSET: 'neutral',
  TRADABLE: 'info',
  INCOME: 'success',
  EXPENSE: 'warning',
  SAVINGS: 'success',
  BUDGET: 'warning',
};

export function toneForKind(kind: Kind): KindTone {
  return KIND_TONE[kind];
}

/**
 * The prototype's kind pills that are a state layer, not a tone wash (TMMAPP-0099, visual contract
 * §9): Cash is `selected` with `link` text, Asset is `hover` with `text2`. The layer is painted over
 * an opaque surface (`layerOverSurface`), so the pair is guaranteed on any canvas. Revolving
 * (warning), Loan (error) and Tradable (info) are the tone washes `KIND_TONE` already gives.
 */
export const KIND_LAYER: Partial<
  Record<Kind, { layer: 'selected' | 'hover'; ink: 'link' | 'text2' }>
> = {
  CASH: { layer: 'selected', ink: 'link' },
  ASSET: { layer: 'hover', ink: 'text2' },
};
